import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  Select,
  Switch,
  HStack,
  VStack,
  Box,
  Text,
  Divider,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Flex,
  Circle,
  Tooltip,
  ButtonGroup,
  SimpleGrid
} from '@chakra-ui/react';
import {
  DeleteIcon,
  EditIcon,
  ExternalLinkIcon,
  CalendarIcon,
  TimeIcon,
  EmailIcon,
  PhoneIcon,
  CheckIcon,
  CloseIcon,
  InfoIcon
} from '@chakra-ui/icons';
import moment from 'moment';

const shadowColor = 'rgba(43,39,35,0.06)';

// Mapeo de estados de cita — tokens Datofy V1
const APPT_STATUS = {
  pending:   { label: 'Pendiente',  bg: 'warn.bg',  color: 'warn.500'  },
  confirmed: { label: 'Confirmada', bg: 'sky.bg',   color: 'sky.ink'   },
  completed: { label: 'Completada', bg: 'ok.bg',    color: 'ok.500'    },
  cancelled: { label: 'Cancelada',  bg: 'danger.bg', color: 'danger.500' },
};

// Tokens Datofy V1 por categoría — misma asignación que Calendar.js / AgendaTodayCard.js
const CATEGORY_TOKENS = {
  clase:      { bg: 'sky.bg',    color: 'sky.ink'    },
  reunion:    { bg: 'lav.bg',    color: 'lav.ink'    },
  evaluacion: { bg: 'butter.bg', color: 'butter.ink' },
  actividad:  { bg: 'mint.bg',   color: 'mint.ink'   },
  importante: { bg: 'rose.bg',   color: 'rose.ink'   },
  otro:       { bg: 'teal.bg',   color: 'teal.ink'   },
};

const EVENT_STATUS_TOKENS = {
  scheduled: { bg: 'sky.bg',    color: 'sky.ink' },
  completed: { bg: 'ok.bg',     color: 'ok.500'  },
  cancelled: { bg: 'danger.bg', color: 'danger.500' },
};

const apptProfileUrl = (studentId) =>
  studentId ? `${window.location.href.split('#')[0]}#/admin/usuario/${studentId}` : null;

// Pill reutilizable estilo Datofy V1
const Pill = ({ bg, color, children, ...rest }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    px="10px"
    py="4px"
    borderRadius="999px"
    bg={bg}
    color={color}
    fontSize="12px"
    fontWeight="700"
    {...rest}
  >
    {children}
  </Box>
);

function EventModal({
  isOpen,
  onClose,
  event = null,
  mode = 'create', // 'create', 'edit', 'view'
  onSave,
  onDelete,
  onAppointmentStatusChange, // (appointment, newStatus, notes) => Promise
  onSaveTeacherNotes, // (appointment, notes) => Promise — guarda la nota del profesor sin cambiar el estado
  loading = false
}) {
  // Estado del formulario
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    all_day: false,
    category: '',
    location: '',
    attendees: '',
    reminder: 15, // minutos antes
    repeat: 'none', // none, daily, weekly, monthly
    status: 'scheduled', // scheduled, completed, cancelled
    visibility: 'public' // public, private
  });

  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(mode === 'create' || mode === 'edit');
  const [apptActionLoading, setApptActionLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [teacherNotesDraft, setTeacherNotesDraft] = useState('');
  const [savingTeacherNotes, setSavingTeacherNotes] = useState(false);

  // Confirmación de eliminación
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  // Confirmación de cancelación de cita
  const { isOpen: isCancelOpen, onOpen: onCancelOpen, onClose: onCancelClose } = useDisclosure();
  const cancelApptRef = React.useRef();
  const cancelRef = React.useRef();

  const toast = useToast();

  // Categorías de eventos
  const categories = [
    { value: '', label: 'Seleccionar categoría' },
    { value: 'clase', label: 'Clase' },
    { value: 'reunion', label: 'Reunión' },
    { value: 'evaluacion', label: 'Evaluación' },
    { value: 'actividad', label: 'Actividad' },
    { value: 'importante', label: 'Importante' },
    { value: 'otro', label: 'Otro' }
  ];

  // Estados del evento
  const statusOptions = [
    { value: 'scheduled', label: 'Programado' },
    { value: 'completed', label: 'Completado' },
    { value: 'cancelled', label: 'Cancelado' }
  ];

  // Opciones de repetición
  const repeatOptions = [
    { value: 'none', label: 'No repetir' },
    { value: 'daily', label: 'Diariamente' },
    { value: 'weekly', label: 'Semanalmente' },
    { value: 'monthly', label: 'Mensualmente' }
  ];

  // Inicializar formulario cuando cambia el evento
  useEffect(() => {
    if (event) {
      const startDate = moment(event.start_date);
      const endDate = moment(event.end_date);

      setFormData({
        title: event.title || '',
        description: event.description || '',
        start_date: startDate.format('YYYY-MM-DD'),
        start_time: event.all_day ? '' : startDate.format('HH:mm'),
        end_date: endDate.format('YYYY-MM-DD'),
        end_time: event.all_day ? '' : endDate.format('HH:mm'),
        all_day: event.all_day || false,
        category: event.category || '',
        location: event.location || '',
        attendees: event.attendees || '',
        reminder: event.reminder_minutes || event.reminder || 15,
        repeat: event.repeat_pattern || event.repeat || 'none',
        status: event.status || 'scheduled',
        visibility: event.visibility || 'public'
      });
    } else {
      // Resetear formulario para nuevo evento
      const now = moment();
      const endTime = moment(now).add(1, 'hour');

      setFormData({
        title: '',
        description: '',
        start_date: now.format('YYYY-MM-DD'),
        start_time: now.format('HH:mm'),
        end_date: now.format('YYYY-MM-DD'),
        end_time: endTime.format('HH:mm'),
        all_day: false,
        category: '',
        location: '',
        attendees: '',
        reminder: 15,
        repeat: 'none',
        status: 'scheduled'
      });
    }

    setEditMode(mode === 'create' || mode === 'edit');
    setErrors({});
    setTeacherNotesDraft(event?._appointment?.teacher_notes || '');
  }, [event, mode, isOpen]);

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'El título es requerido';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'La fecha de inicio es requerida';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'La fecha de fin es requerida';
    }

    if (!formData.all_day) {
      if (!formData.start_time) {
        newErrors.start_time = 'La hora de inicio es requerida';
      }

      if (!formData.end_time) {
        newErrors.end_time = 'La hora de fin es requerida';
      }

      // Validar que la fecha/hora de fin sea posterior a la de inicio
      if (formData.start_date && formData.start_time && formData.end_date && formData.end_time) {
        const startDateTime = moment(`${formData.start_date} ${formData.start_time}`);
        const endDateTime = moment(`${formData.end_date} ${formData.end_time}`);

        if (endDateTime.isSameOrBefore(startDateTime)) {
          newErrors.end_time = 'La hora de fin debe ser posterior a la de inicio';
        }
      }
    } else {
      // Para eventos de todo el día, validar que la fecha de fin sea igual o posterior
      if (formData.start_date && formData.end_date) {
        const startDate = moment(formData.start_date);
        const endDate = moment(formData.end_date);

        if (endDate.isBefore(startDate)) {
          newErrors.end_date = 'La fecha de fin debe ser igual o posterior a la de inicio';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = () => {
    if (!validateForm()) {
      toast({
        title: 'Error de validación',
        description: 'Por favor corrige los errores en el formulario',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Preparar datos para envío
    let startDateTime, endDateTime;

    if (formData.all_day) {
      startDateTime = moment(formData.start_date).startOf('day');
      endDateTime = moment(formData.end_date).endOf('day');
    } else {
      startDateTime = moment(`${formData.start_date} ${formData.start_time}`);
      endDateTime = moment(`${formData.end_date} ${formData.end_time}`);
    }

    const eventData = {
      title: formData.title,
      description: formData.description,
      start_date: startDateTime.toISOString(),
      end_date: endDateTime.toISOString(),
      all_day: formData.all_day,
      location: formData.location,
      category: formData.category,
      status: formData.status,
      visibility: formData.visibility,
      repeat_pattern: formData.repeat,
      reminder_minutes: formData.reminder,
      attendees: formData.attendees.split(',').map(email => email.trim()).filter(email => email).join(',')
    };

    onSave(eventData);
  };

  // Manejar eliminación
  const handleDelete = () => {
    onDelete(event.id);
    onDeleteClose();
  };

  // Acciones sobre citas agendadas
  const handleApptAction = async (newStatus, notes = '') => {
    if (!onAppointmentStatusChange || !event?._appointment) return;
    setApptActionLoading(true);
    try {
      await onAppointmentStatusChange(event._appointment, newStatus, notes);
    } finally {
      setApptActionLoading(false);
    }
  };

  const handleApptCancel = async () => {
    await handleApptAction('cancelled', cancelReason);
    onCancelClose();
    setCancelReason('');
  };

  // Guarda la nota del profesor sin cambiar el estado de la cita ni cerrar el modal
  const handleSaveTeacherNotes = async () => {
    if (!onSaveTeacherNotes || !event?._appointment) return;
    setSavingTeacherNotes(true);
    try {
      await onSaveTeacherNotes(event._appointment, teacherNotesDraft);
    } finally {
      setSavingTeacherNotes(false);
    }
  };

  // Obtener tokens de categoría
  const getCategoryTokens = (category) => CATEGORY_TOKENS[category] || CATEGORY_TOKENS.otro;

  // Obtener tokens de estado
  const getStatusTokens = (status) => EVENT_STATUS_TOKENS[status] || EVENT_STATUS_TOKENS.scheduled;

  // Datos de la cita — disponibles tanto para la cabecera como para el footer
  const apt = event?._appointment || null;
  const apptStatus = apt ? (APPT_STATUS[apt.status] || APPT_STATUS.pending) : null;
  const isApptPast = apt ? new Date(`${apt.appointment_date}T${apt.start_time}`) < new Date() : false;
  const canActOnAppt = apt ? (!isApptPast && apt.status !== 'cancelled' && apt.status !== 'completed') : false;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg="cream.bg" borderRadius="18px" fontFamily="jakarta">
          <ModalHeader borderBottom="1px solid" borderColor="cream.line" color="ink.900" fontWeight="800">
            <HStack justify="space-between" align="center">
              <HStack spacing={2} flexWrap="wrap">
                <Text>
                  {mode === 'create' ? 'Nuevo Evento' :
                   mode === 'edit' ? 'Editar Evento' :
                   event?.category === 'cita' ? 'Cita Agendada' : 'Detalles del Evento'}
                </Text>
                {!editMode && apt && apptStatus && (
                  <Pill bg={apptStatus.bg} color={apptStatus.color}>
                    {apptStatus.label}
                  </Pill>
                )}
                {!editMode && apt && isApptPast && apt.status === 'pending' && (
                  <Pill bg="cream.bg2" color="ink.400">Pasada</Pill>
                )}
              </HStack>
              {!editMode && apt?.confirmation_code && (
                <Tooltip label="Código de confirmación">
                  <Text fontSize="xs" color="ink.400" fontFamily="mono" fontWeight="700">
                    #{apt.confirmation_code}
                  </Text>
                </Tooltip>
              )}
              {mode === 'view' && event?.category !== 'cita' && (
                <HStack>
                  <Button
                    size="sm"
                    leftIcon={<EditIcon />}
                    bg="coral.500"
                    color="white"
                    fontWeight="600"
                    borderRadius="10px"
                    _hover={{ bg: 'coral.600' }}
                    _active={{ bg: 'coral.700' }}
                    onClick={() => setEditMode(true)}
                  >
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor="danger.500"
                    color="danger.500"
                    fontWeight="600"
                    borderRadius="10px"
                    _hover={{ bg: 'danger.bg' }}
                    leftIcon={<DeleteIcon />}
                    onClick={onDeleteOpen}
                  >
                    Eliminar
                  </Button>
                </HStack>
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* ── Vista de solo lectura ── */}
              {!editMode && event && (
                event.category === 'cita' && event._appointment
                  ? /* ── Panel de Cita Agendada ── */
                    (() => {
                      const profileUrl = apptProfileUrl(apt.student_id);
                      const initials = (apt.student_name || '')
                        .split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');

                      return (
                        <VStack align="stretch" spacing={3}>

                          {/* Alumno */}
                          <Box
                            bg="white"
                            border="1px solid"
                            borderColor="cream.line"
                            borderRadius="14px"
                            boxShadow={`0 4px 6px -1px ${shadowColor}`}
                            p={4}
                          >
                            <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                              Alumno
                            </Text>
                            <HStack spacing={3} align="flex-start">
                              <Circle size="40px" bg="coral.500" color="white" fontSize="md" fontWeight="bold" flexShrink={0}>
                                {initials || '?'}
                              </Circle>
                              <VStack align="start" spacing={0} flex="1">
                                <Text fontWeight="700" fontSize="md" color="ink.900">
                                  {apt.student_name || apt.student_rut}
                                </Text>
                                {apt.student_rut && (
                                  <Text fontSize="sm" color="ink.400">
                                    RUT: {apt.student_rut}
                                  </Text>
                                )}
                              </VStack>
                              {profileUrl && (
                                <Tooltip label="Ver perfil completo del alumno">
                                  <Button
                                    as="a"
                                    href={profileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="xs"
                                    variant="outline"
                                    borderColor="coral.500"
                                    color="coral.700"
                                    _hover={{ bg: 'coral.tint' }}
                                    rightIcon={<ExternalLinkIcon />}
                                    flexShrink={0}
                                  >
                                    Ver perfil
                                  </Button>
                                </Tooltip>
                              )}
                            </HStack>
                          </Box>

                          {/* Asunto + Fecha y hora — lado a lado */}
                          <SimpleGrid columns={{ base: 1, md: apt.subject ? 2 : 1 }} spacing={3}>
                            {apt.subject && (
                              <Box
                                bg="white"
                                border="1px solid"
                                borderColor="cream.line"
                                borderRadius="14px"
                                boxShadow={`0 4px 6px -1px ${shadowColor}`}
                                p={4}
                              >
                                <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                                  Asunto
                                </Text>
                                <Text fontSize="sm" color="ink.900" fontWeight="500">
                                  {apt.subject}
                                </Text>
                              </Box>
                            )}

                            <Box
                              bg="white"
                              border="1px solid"
                              borderColor="cream.line"
                              borderRadius="14px"
                              boxShadow={`0 4px 6px -1px ${shadowColor}`}
                              p={4}
                            >
                              <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                                Fecha y hora
                              </Text>
                              <VStack align="start" spacing={2}>
                                <HStack spacing={2}>
                                  <CalendarIcon color="coral.500" w="14px" h="14px" />
                                  <Text fontSize="sm" color="ink.900" fontWeight="500">
                                    {moment(apt.appointment_date).locale('es').format('dddd, D [de] MMMM [de] YYYY')}
                                  </Text>
                                </HStack>
                                <HStack spacing={2}>
                                  <TimeIcon color="coral.500" w="14px" h="14px" />
                                  <Text fontSize="sm" color="ink.900">
                                    {apt.start_time?.slice(0, 5)} — {apt.end_time?.slice(0, 5)}
                                  </Text>
                                </HStack>
                                {apt.teacher_name && (
                                  <HStack spacing={2}>
                                    <InfoIcon color="coral.500" w="14px" h="14px" />
                                    <Text fontSize="sm" color="ink.900">
                                      Prof. {apt.teacher_name}
                                    </Text>
                                  </HStack>
                                )}
                              </VStack>
                            </Box>
                          </SimpleGrid>

                          {/* Apoderado */}
                          <Box
                            bg="white"
                            border="1px solid"
                            borderColor="cream.line"
                            borderRadius="14px"
                            boxShadow={`0 4px 6px -1px ${shadowColor}`}
                            p={4}
                          >
                            <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                              Apoderado
                            </Text>
                            <VStack align="start" spacing={2}>
                              <HStack spacing={2}>
                                <InfoIcon color="sky.ink" w="14px" h="14px" />
                                <Text fontSize="sm" color="ink.900" fontWeight="500">
                                  {apt.guardian_name}
                                </Text>
                              </HStack>
                              {apt.guardian_email && (
                                <HStack spacing={2}>
                                  <EmailIcon color="sky.ink" w="14px" h="14px" />
                                  <Text fontSize="sm" color="ink.900">{apt.guardian_email}</Text>
                                </HStack>
                              )}
                              {apt.guardian_phone && (
                                <HStack spacing={2}>
                                  <PhoneIcon color="sky.ink" w="14px" h="14px" />
                                  <Text fontSize="sm" color="ink.900">{apt.guardian_phone}</Text>
                                </HStack>
                              )}
                            </VStack>
                          </Box>

                          {/* Nota de quien agendó (apt.notes) */}
                          {apt.notes && (
                            <Box
                              border="1px solid"
                              borderColor="cream.line"
                              bg="butter.bg"
                              borderRadius="14px"
                              p={4}
                            >
                              <Text fontSize="11px" fontWeight="800" color="butter.ink" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                                Nota de quien agendó
                              </Text>
                              <Text fontSize="sm" color="ink.900" fontStyle="italic">
                                "{apt.notes}"
                              </Text>
                            </Box>
                          )}

                          {/* Notas del profesor — editable */}
                          <Box
                            border="1px solid"
                            borderColor="cream.line"
                            bg="sky.bg"
                            borderRadius="14px"
                            p={4}
                          >
                            <Text fontSize="11px" fontWeight="800" color="sky.ink" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                              Notas del profesor
                            </Text>
                            {onSaveTeacherNotes ? (
                              <VStack align="stretch" spacing={2}>
                                <Textarea
                                  value={teacherNotesDraft}
                                  onChange={(e) => setTeacherNotesDraft(e.target.value)}
                                  placeholder="Agrega una nota sobre esta cita..."
                                  rows={3}
                                  bg="white"
                                  fontSize="sm"
                                  focusBorderColor="sky.ink"
                                />
                                <Flex justify="flex-end">
                                  <Button
                                    size="xs"
                                    bg="sky.ink"
                                    color="white"
                                    fontWeight="600"
                                    _hover={{ opacity: 0.9 }}
                                    isLoading={savingTeacherNotes}
                                    isDisabled={teacherNotesDraft === (apt.teacher_notes || '')}
                                    onClick={handleSaveTeacherNotes}
                                  >
                                    Guardar nota
                                  </Button>
                                </Flex>
                              </VStack>
                            ) : (
                              <Text fontSize="sm" color="ink.900" fontStyle={apt.teacher_notes ? 'normal' : 'italic'}>
                                {apt.teacher_notes || 'Sin notas del profesor'}
                              </Text>
                            )}
                          </Box>

                        </VStack>
                      );
                    })()
                  : /* ── Vista genérica de evento ── */
                    <VStack align="stretch" spacing={3}>
                      <Flex
                        bg="cream.bg2"
                        border="1px solid"
                        borderColor="cream.line"
                        borderRadius="14px"
                        px={4} py={3}
                        justify="space-between"
                        align="center"
                      >
                        <Text fontWeight="800" fontSize="md" color="ink.900">
                          {event.title}
                        </Text>
                        {event.category && (
                          <Pill {...getCategoryTokens(event.category)}>
                            {categories.find(c => c.value === event.category)?.label}
                          </Pill>
                        )}
                      </Flex>

                      {event.description && (
                        <Box
                          bg="white"
                          border="1px solid"
                          borderColor="cream.line"
                          borderRadius="14px"
                          boxShadow={`0 4px 6px -1px ${shadowColor}`}
                          p={4}
                        >
                          <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                            Descripción
                          </Text>
                          <Text fontSize="sm" color="ink.900">{event.description}</Text>
                        </Box>
                      )}

                      <Box
                        bg="white"
                        border="1px solid"
                        borderColor="cream.line"
                        borderRadius="14px"
                        boxShadow={`0 4px 6px -1px ${shadowColor}`}
                        p={4}
                      >
                        <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                          Fecha y hora
                        </Text>
                        <HStack spacing={2}>
                          <CalendarIcon color="coral.500" w="14px" h="14px" />
                          <Text fontSize="sm" color="ink.900" fontWeight="500">
                            {event.all_day
                              ? `${moment(event.start_date).format('DD/MM/YYYY')} - Todo el día`
                              : `${moment(event.start_date).format('DD/MM/YYYY HH:mm')} - ${moment(event.end_date).format('DD/MM/YYYY HH:mm')}`
                            }
                          </Text>
                        </HStack>
                      </Box>

                      {event.location && (
                        <Box
                          bg="white"
                          border="1px solid"
                          borderColor="cream.line"
                          borderRadius="14px"
                          boxShadow={`0 4px 6px -1px ${shadowColor}`}
                          p={4}
                        >
                          <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                            Ubicación
                          </Text>
                          <Text fontSize="sm" color="ink.900">{event.location}</Text>
                        </Box>
                      )}

                      <Box>
                        <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                          Estado
                        </Text>
                        <Pill {...getStatusTokens(event.status)}>
                          {statusOptions.find(s => s.value === event.status)?.label}
                        </Pill>
                      </Box>
                    </VStack>
              )}

              {/* Formulario de edición */}
              {editMode && (
                <>
                  <FormControl isInvalid={errors.title}>
                    <FormLabel color="ink.900">Título *</FormLabel>
                    <Input
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Título del evento"
                      bg="white"
                      focusBorderColor="coral.500"
                    />
                    <FormErrorMessage>{errors.title}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel color="ink.900">Descripción</FormLabel>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Descripción del evento"
                      rows={3}
                      bg="white"
                      focusBorderColor="coral.500"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color="ink.900">Categoría</FormLabel>
                    <Select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      bg="white"
                      focusBorderColor="coral.500"
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel color="ink.900">Visibilidad</FormLabel>
                    <Select
                      value={formData.visibility}
                      onChange={(e) => handleInputChange('visibility', e.target.value)}
                      bg="white"
                      focusBorderColor="coral.500"
                    >
                      <option value="public">Público — visible para todos</option>
                      <option value="private">Privado — solo yo lo veo</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <HStack align="center">
                      <FormLabel color="ink.900" mb={0}>Todo el día</FormLabel>
                      <Switch
                        isChecked={formData.all_day}
                        onChange={(e) => handleInputChange('all_day', e.target.checked)}
                        colorScheme="orange"
                      />
                    </HStack>
                  </FormControl>

                  <HStack spacing={4}>
                    <FormControl isInvalid={errors.start_date}>
                      <FormLabel color="ink.900">Fecha inicio *</FormLabel>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        bg="white"
                        focusBorderColor="coral.500"
                      />
                      <FormErrorMessage>{errors.start_date}</FormErrorMessage>
                    </FormControl>

                    {!formData.all_day && (
                      <FormControl isInvalid={errors.start_time}>
                        <FormLabel color="ink.900">Hora inicio *</FormLabel>
                        <Input
                          type="time"
                          value={formData.start_time}
                          onChange={(e) => handleInputChange('start_time', e.target.value)}
                          bg="white"
                          focusBorderColor="coral.500"
                        />
                        <FormErrorMessage>{errors.start_time}</FormErrorMessage>
                      </FormControl>
                    )}
                  </HStack>

                  <HStack spacing={4}>
                    <FormControl isInvalid={errors.end_date}>
                      <FormLabel color="ink.900">Fecha fin *</FormLabel>
                      <Input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => handleInputChange('end_date', e.target.value)}
                        bg="white"
                        focusBorderColor="coral.500"
                      />
                      <FormErrorMessage>{errors.end_date}</FormErrorMessage>
                    </FormControl>

                    {!formData.all_day && (
                      <FormControl isInvalid={errors.end_time}>
                        <FormLabel color="ink.900">Hora fin *</FormLabel>
                        <Input
                          type="time"
                          value={formData.end_time}
                          onChange={(e) => handleInputChange('end_time', e.target.value)}
                          bg="white"
                          focusBorderColor="coral.500"
                        />
                        <FormErrorMessage>{errors.end_time}</FormErrorMessage>
                      </FormControl>
                    )}
                  </HStack>

                  <FormControl>
                    <FormLabel color="ink.900">Ubicación</FormLabel>
                    <Input
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="Ubicación del evento"
                      bg="white"
                      focusBorderColor="coral.500"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color="ink.900">Estado</FormLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      bg="white"
                      focusBorderColor="coral.500"
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <Divider borderColor="cream.line" />

                  <FormControl>
                    <FormLabel color="ink.900">Repetir</FormLabel>
                    <Select
                      value={formData.repeat}
                      onChange={(e) => handleInputChange('repeat', e.target.value)}
                      bg="white"
                      focusBorderColor="coral.500"
                    >
                      {repeatOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel color="ink.900">Recordatorio (minutos antes)</FormLabel>
                    <Select
                      value={formData.reminder}
                      onChange={(e) => handleInputChange('reminder', parseInt(e.target.value))}
                      bg="white"
                      focusBorderColor="coral.500"
                    >
                      <option value={0}>Sin recordatorio</option>
                      <option value={5}>5 minutos</option>
                      <option value={15}>15 minutos</option>
                      <option value={30}>30 minutos</option>
                      <option value={60}>1 hora</option>
                      <option value={120}>2 horas</option>
                      <option value={1440}>1 día</option>
                    </Select>
                  </FormControl>
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="cream.line" justifyContent="space-between">
            <ButtonGroup spacing={2} flexWrap="wrap">
              {!editMode && apt && canActOnAppt && onAppointmentStatusChange && (
                <>
                  {apt.status === 'pending' && (
                    <Button
                      size="sm"
                      bg="sky.ink"
                      color="white"
                      _hover={{ opacity: 0.9 }}
                      leftIcon={<CheckIcon />}
                      isLoading={apptActionLoading}
                      onClick={() => handleApptAction('confirmed')}
                    >
                      Confirmar cita
                    </Button>
                  )}
                  {apt.status === 'confirmed' && (
                    <Button
                      size="sm"
                      bg="ok.500"
                      color="white"
                      _hover={{ opacity: 0.9 }}
                      leftIcon={<CheckIcon />}
                      isLoading={apptActionLoading}
                      onClick={() => handleApptAction('completed')}
                    >
                      Marcar completada
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor="danger.500"
                    color="danger.500"
                    _hover={{ bg: 'danger.bg' }}
                    leftIcon={<CloseIcon w="10px" />}
                    isDisabled={apptActionLoading}
                    onClick={onCancelOpen}
                  >
                    Cancelar cita
                  </Button>
                </>
              )}
            </ButtonGroup>
            <HStack spacing={3}>
              <Button variant="ghost" color="ink.400" _hover={{ bg: 'cream.bg2', color: 'ink.700' }} onClick={onClose}>
                {editMode ? 'Cancelar' : 'Cerrar'}
              </Button>
              {editMode && (
                <Button
                  bg="coral.500"
                  color="white"
                  fontWeight="600"
                  borderRadius="10px"
                  _hover={{ bg: 'coral.600' }}
                  _active={{ bg: 'coral.700' }}
                  onClick={handleSubmit}
                  isLoading={loading}
                >
                  {mode === 'create' ? 'Crear Evento' : 'Guardar Cambios'}
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Diálogo de cancelación de cita */}
      <AlertDialog
        isOpen={isCancelOpen}
        leastDestructiveRef={cancelApptRef}
        onClose={onCancelClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="cream.bg" borderRadius="16px" fontFamily="jakarta">
            <AlertDialogHeader fontSize="lg" fontWeight="800" color="ink.900">
              Cancelar Cita
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={3} align="stretch">
                <Text color="ink.700">¿Confirmar cancelación de la cita?</Text>
                <FormControl>
                  <FormLabel fontSize="sm" color="ink.900">Motivo (opcional)</FormLabel>
                  <Textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Explique el motivo de la cancelación..."
                    rows={3}
                    size="sm"
                    bg="white"
                    focusBorderColor="coral.500"
                  />
                </FormControl>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelApptRef} variant="ghost" color="ink.400" _hover={{ bg: 'cream.bg2', color: 'ink.700' }} onClick={onCancelClose}>
                Volver
              </Button>
              <Button
                bg="danger.500"
                color="white"
                fontWeight="600"
                _hover={{ opacity: 0.9 }}
                onClick={handleApptCancel}
                ml={3}
                isLoading={apptActionLoading}
              >
                Sí, cancelar cita
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Diálogo de confirmación de eliminación */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="cream.bg" borderRadius="16px" fontFamily="jakarta">
            <AlertDialogHeader fontSize="lg" fontWeight="800" color="ink.900">
              Eliminar Evento
            </AlertDialogHeader>

            <AlertDialogBody color="ink.700">
              ¿Estás seguro de que deseas eliminar este evento? Esta acción no se puede deshacer.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" color="ink.400" _hover={{ bg: 'cream.bg2', color: 'ink.700' }} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button
                bg="danger.500"
                color="white"
                fontWeight="600"
                _hover={{ opacity: 0.9 }}
                onClick={handleDelete}
                ml={3}
                isLoading={loading}
              >
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default EventModal;
